import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Phone,
  Mail,
  ExternalLink,
  ArrowRight,
  AlertCircle,
  PhoneCall,
  Globe,
  Car,
  CheckCircle2,
} from "lucide-react";
import { company, icbcRenewal } from "../mock";
import RenewalForm from "../components/RenewalForm";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";

const optionIconMap = { PhoneCall, Globe };

const ICBCRenewal = () => {
  const formRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="aurora bg-[#8ec4ec] h-80 w-80 -top-20 -left-16" />
        <div className="aurora bg-[#8ec4ec] h-80 w-80 top-0 right-0" />
        <div className="max-w-4xl mx-auto px-6 pt-16 pb-10 text-center relative z-10 fade-up">
          <div className="h-16 w-16 mx-auto rounded-2xl bg-gradient-to-br from-[#0066b3] to-[#003c7a] flex items-center justify-center text-white mb-6 shadow-lg">
            <RefreshCw size={30} />
          </div>
          <nav className="text-sm text-slate-500 mb-4 flex items-center justify-center gap-2">
            <Link to="/" className="hover:text-[#0066b3]">Home</Link>
            <span>/</span>
            <span className="text-[#0066b3] font-medium">ICBC Renewal Options</span>
          </nav>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[#0a2b52]">
            ICBC <span className="brand-gradient-text">Renewal Options</span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
            With Cloverdale Insurance, you can complete your ICBC insurance
            renewal online without leaving your home. We make ICBC renewals easy.
          </p>
        </div>
      </section>

      {/* TWO OPTION CARDS */}
      <section className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-6">
          {icbcRenewal.options.map((opt) => {
            const Icon = optionIconMap[opt.icon] || RefreshCw;
            return (
              <div key={opt.title} className="glass rounded-3xl p-8 card-hover flex flex-col">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#0066b3] to-[#003c7a] flex items-center justify-center text-white mb-5 shadow-lg">
                  <Icon size={26} />
                </div>
                <h2 className="font-display text-2xl font-bold text-[#0a2b52]">
                  {opt.title}
                </h2>
                <p className="text-slate-600 mt-3 leading-relaxed flex-1">
                  {opt.desc}
                </p>
                {opt.action === "portal" ? (
                  <a
                    href={icbcRenewal.portalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="brand-btn mt-6 px-6 py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 self-start"
                  >
                    <ExternalLink size={18} /> {opt.cta}
                  </a>
                ) : (
                  <button
                    onClick={scrollToForm}
                    className="brand-btn mt-6 px-6 py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 self-start"
                  >
                    <RefreshCw size={18} /> {opt.cta}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* RENEWAL REQUEST FORM */}
      <section ref={formRef} className="max-w-4xl mx-auto px-6 pt-20 scroll-mt-24">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="text-[#0066b3] font-semibold text-sm tracking-wider uppercase">
            Request Renewal
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[#0a2b52] mt-2">
            Request Your ICBC Policy Renewal
          </h2>
          <p className="text-slate-600 mt-3">
            Fill in your basic information below and a Cloverdale agent will
            contact you directly to complete your renewal.
          </p>
        </div>
        <div className="glass-strong rounded-3xl p-7 md:p-9">
          <RenewalForm />
        </div>
      </section>

      {/* INFO */}
      <section className="max-w-4xl mx-auto px-6 pt-16">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0a2b52]">
          Renewing your auto insurance with ICBC
        </h2>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Eligible customers can renew their policies directly through the ICBC
          website — a convenient online option. Please note this doesn't apply to
          all policies; explore the FAQs below for more information.
        </p>
        <p className="text-slate-600 mt-4 leading-relaxed">
          If you're not eligible for ICBC's online renewal, there's another easy
          method: let your broker handle it for you. Simply complete our online
          renewal request form above or give us a call. We'll get your written
          consent by email and then renew your policy — it's that easy!
        </p>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {["Fast, proactive renewals", "Renew online, by phone or in person", "Documents emailed or mailed to you", "Open 7 days a week to help"].map((t) => (
            <div key={t} className="flex items-start gap-3">
              <CheckCircle2 size={22} className="text-[#0066b3] shrink-0 mt-0.5" />
              <span className="text-slate-700">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Important note */}
      <section className="max-w-4xl mx-auto px-6 pt-12">
        <div className="glass-strong rounded-3xl p-6 md:p-8 border-l-4 border-[#0066b3]">
          <div className="flex items-start gap-4">
            <AlertCircle size={26} className="text-[#0066b3] shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-lg font-bold text-[#0a2b52] mb-2">
                Please Note
              </h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                {icbcRenewal.note}
              </p>
              <div className="mt-5 flex flex-wrap gap-4">
                <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 text-[#0066b3] font-semibold hover:text-[#003c7a]">
                  <Phone size={18} /> {company.phone}
                </a>
                <a href={`mailto:${company.altEmail}`} className="flex items-center gap-2 text-[#0066b3] font-semibold hover:text-[#003c7a]">
                  <Mail size={18} /> {company.altEmail}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 pt-16">
        <div className="text-center mb-8">
          <span className="text-[#0066b3] font-semibold text-sm tracking-wider uppercase">
            Good to know
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[#0a2b52] mt-2">
            ICBC Renewal FAQ
          </h2>
        </div>
        <div className="glass-strong rounded-3xl p-4 md:p-6">
          <Accordion type="single" collapsible className="w-full">
            {icbcRenewal.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-slate-200/70">
                <AccordionTrigger className="text-[#0a2b52] font-semibold text-base hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed text-[15px]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pt-16">
        <div className="glass-strong rounded-3xl p-10 md:p-12 text-center relative overflow-hidden">
          <div className="aurora bg-[#cfe4f6] h-64 w-64 -bottom-24 -right-10" />
          <Car size={40} className="text-[#0066b3] mx-auto mb-4 relative" />
          <h2 className="font-display text-3xl font-extrabold text-[#0a2b52] relative">
            Looking for insurance assistance? We're here to help!
          </h2>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto relative">
            Contact us today and one of our agents will happily assist you with
            your renewal — 7 days a week.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-4 relative">
            <Link
              to="/contact"
              className="brand-btn inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"
            >
              Get In Touch <ArrowRight size={18} />
            </Link>
            <a
              href={`tel:${company.phoneRaw}`}
              className="glass inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-[#0a2b52] hover:text-[#0066b3] transition-colors"
            >
              <Phone size={18} /> {company.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ICBCRenewal;
